import React, { useRef } from 'react';
import {
  TextField,
  List,
  ListItem,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  ListItemButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import T from 'i18n';
import PropTypes from 'prop-types';
import { Spinner } from 'features/common';

interface SearchWithListProps {
  searchInput: string;
  searchResults: any[];
  isLoading: boolean;
  error: string | null;
  selectedIndex: number | null;
  onSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  onResultClick: (result: any, index: number) => void;
}

const SearchWithList: React.FC<SearchWithListProps> = ({
  searchInput,
  searchResults,
  isLoading,
  error,
  selectedIndex,
  onSearchInputChange,
  onClearSearch,
  onResultClick,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleItemClick = (result: any, index: number) => {
    onResultClick(result, index);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchInputChange(event);
  };

  const handleClear = () => {
    onClearSearch();
    // Focus the input field after clearing
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  };

  const renderSearchResults = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Spinner />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (searchInput.trim().length > 0) {
      // Check if query contains non-alphabetical characters (excluding spaces)
      const hasNonAlphabetical = /[^a-zA-Z\s]/.test(searchInput);
      
      // Show validation message only if input is less than 3 characters AND contains only alphabetical characters
      if (!hasNonAlphabetical && searchInput.trim().length < 3) {
        return (
          <Box sx={{ p: 2 }}>
            <Typography>{T.translate('viewing.searchPageMinThreeValidation')}</Typography>
          </Box>
        );
      }
    }

    const highlightSearchText = (text: string, searchTerm: string): React.ReactNode => {
      if (!text || !searchTerm || searchTerm.trim().length === 0) {
        return text;
      }

      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm.length === 0) {
        return text;
      }

      const escapedSearchTerm = trimmedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

      // Split the text by the search term while preserving the delimiters
      const parts = text.split(regex);

      return (
        <>
          {parts.map((part, index) => {
            const isMatch = part.toLowerCase() === trimmedSearchTerm.toLowerCase();
            return isMatch ? (
              <span
                key={index}
                style={{
                  backgroundColor: '#B3D9FF',
                  padding: '1px 2px',
                }}
              >
                {part}
              </span>
            ) : (
              part
            );
          })}
        </>
      );
    };

    return (
      <>
        {searchResults.length > 0 && (
          <div style={{ fontSize: '12px', color: '#888', margin: '0.5rem', textAlign: 'right' }}>
            <span style={{ textTransform: 'uppercase' }}>
              {T.translate('generic.totalResults')}
            </span>
            :{' '}
            <span
              style={{
                display: 'inline-block',
                fontSize: '10px',
                backgroundColor: '#4567e5',
                opacity: '0.75',
                color: '#fff',
                width: 'auto',
                height: 'auto',
                textAlign: 'center',
                padding: '3px 6px',
                borderRadius: '5px',
              }}
            >
              {searchResults.length}
            </span>
          </div>
        )}
        <Box
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto',
            width: '100%',
          }}
        >
          <List sx={{ width: '100%' }}>
            {searchResults.map((result, index) => (
              <ListItem
                key={`search-result-${index}-${result.pageNumber}`}
                disablePadding
                data-index={index}
                sx={{
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  display: 'block',
                }}
              >
                <ListItemButton
                  onClick={() => handleItemClick(result, index)}
                  selected={selectedIndex === index}
                  disableRipple={true}
                  disableTouchRipple={true}
                  sx={{
                    display: 'block',
                    padding: '12px 16px',
                    backgroundColor: selectedIndex === index ? '#B2C3EB46' : undefined,
                    '&.Mui-selected': {
                      backgroundColor: '#B2C3EB46',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#B2C3EB46',
                    },
                    transition: 'none',
                    '& *': {
                      transition: 'none',
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {highlightSearchText(
                      result.context ||
                        result.text ||
                        result.snippet ||
                        'No preview available',
                      searchInput,
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      fontWeight: 'medium',
                      fontSize: '10px',
                    }}
                  >
                    {T.translate('case.page')} {result.pageNumber || 'Unknown'}
                  </Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pr: 1.5, pl: 1 }}>
      <TextField
        fullWidth
        inputRef={inputRef}
        variant="outlined"
        size="small"
        placeholder={T.translate('viewing.searchPlaceholder')}
        value={searchInput}
        onChange={handleInputChange}
        autoFocus
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
          },
        }}
        InputProps={{
          sx: {
            '& .MuiInputBase-input': {
              userSelect: 'text',
            },
          },
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton aria-label="clear search" onClick={handleClear} edge="end" size="small">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Box sx={{ flex: 1, overflow: 'auto' }}>{renderSearchResults()}</Box>
    </Box>
  );
};

SearchWithList.propTypes = {
  searchInput: PropTypes.string.isRequired,
  searchResults: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  selectedIndex: PropTypes.number,
  onSearchInputChange: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onResultClick: PropTypes.func.isRequired,
};

export default SearchWithList;
